// src/components/TaskAssistantSuggester.tsx
import React, { useState, useEffect } from 'react';
import { 
  Paper, 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemButton,
  ListItemText,
  Popper
} from '@mui/material';
import { AutoAwesome as SuggestionIcon } from '@mui/icons-material';

// Define common task phrases
const COMMON_TASK_PHRASES = [
    // Communication
    "Get back to",
    "Email",
    "Follow up with",
    "Call",
    "Reply to",
    "Schedule meeting with",
    "Respond to message from",
    "Check in with",
    "Confirm with",
    "Notify",
    "Discuss",
  
    // Action
    "Complete",
    "Prepare",
    "Review",
    "Submit",
    "Finalize",
    "Update",
    "Create",
    "Draft",
    "Send",
    "Deliver",
  
    // Project
    "Research",
    "Implement",
    "Test",
    "Build",
    "Design",
    "Develop",
    "Launch",
    "Fix issue with",
    "Set up",
    "Publish",
  
    // Administrative
    "Organize",
    "Book",
    "Order",
    "Arrange",
    "File",
    "Print",
    "Upload",
    "Download",
    "Share",
    "Document",
  
    // Follow-up
    "Check status of",
    "Verify",
    "Monitor",
    "Look into",
    "Investigate",
    "Find out if",
    "Ask about",
    "Remind",
  
    // Delegation & Assistance
    "Assign to",
    "Request help with",
    "Delegate to",
    "Ask [team member] to",
    "Send for review",
  
    // General Catch-All Phrases
    "Handle",
    "Take care of",
    "Sort out",
    "Follow through on",
    "Arrange for",
  
    // Scheduling & Time-Specific
    "Schedule for later",
    "Postpone until",
    "Reschedule",
    "Set deadline for",
    "Plan for next week",
  
    // Quick Confirmation Actions
    "Mark as done",
    "Approve",
    "Acknowledge",
    "Confirm receipt of",
  
    // Personal & Well-being
    "Take a break",
    "Check on",
    "Hydrate",
    "Stretch",
  
    // Finance & Payments
    "Pay",
    "Invoice",
    "Submit expense report",
    "Reimburse",
    "Process payment",
    "Approve payment",
    "Check balance",
    "Transfer funds",
    "Deposit",
    "Withdraw",
    "Follow up on invoice",
    "Request payment",
    "Send invoice to",
    "Review financials",
  
    // Budgeting & Reporting
    "Review budget",
    "Track expenses",
    "Update financial report",
    "Reconcile accounts",
    "Analyze spending",
    "Generate report",
    "Prepare financial statement",
    "Audit records",
    "Verify transaction",
    "Confirm payment received",
  
    // Subscriptions & Recurring Payments
    "Cancel subscription",
    "Renew subscription",
    "Update billing information",
    "Check upcoming charges",
    "Modify payment plan",
  
    // Banking & Investments
    "Schedule loan payment",
    "Review investment portfolio",
    "Check interest rates",
    "Evaluate loan options",
    "Manage savings goals"
  ];

interface TaskAssistantSuggesterProps {
  inputValue: string;
  anchorEl: HTMLElement | null;
  onSuggestionSelect: (suggestion: string) => void;
}

const TaskAssistantSuggester: React.FC<TaskAssistantSuggesterProps> = ({
  inputValue,
  anchorEl,
  onSuggestionSelect
}) => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  
  useEffect(() => {
    if (!inputValue || inputValue.includes('#') || inputValue.includes('@')) {
      // Don't show suggestions when typing hashtags or mentions
      setSuggestions([]);
      return;
    }
    
    // Get the current word being typed
    const words = inputValue.split(' ');
    const currentWord = words[words.length - 1].toLowerCase();
    
    // If we're at the beginning of input or just started a new word
    if (words.length <= 1 || (words.length > 1 && currentWord.length > 0)) {
      // Find matching suggestions
      const matches = COMMON_TASK_PHRASES.filter(phrase => 
        phrase.toLowerCase().startsWith(currentWord)
      );
      
      // Limit to 5 suggestions for better UX
      setSuggestions(matches.slice(0, 5));
    } else {
      setSuggestions([]);
    }
  }, [inputValue]);
  
  const handleSuggestionClick = (suggestion: string) => {
    onSuggestionSelect(suggestion);
  };
  
  if (suggestions.length === 0) return null;
  
  return (
    <Popper
      open={suggestions.length > 0}
      anchorEl={anchorEl}
      placement="bottom-start"
      style={{ width: anchorEl?.offsetWidth, zIndex: 1300 }}
    >
      <Paper elevation={3} sx={{ mt: 1 }}>
        <List dense>
          <ListItem>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SuggestionIcon fontSize="small" color="info" />
              <Typography variant="caption" color="text.secondary">
                Task Assistant
              </Typography>
            </Box>
          </ListItem>
          {suggestions.map((suggestion, index) => (
            <ListItem key={index} disablePadding>
              <ListItemButton onClick={() => handleSuggestionClick(suggestion)}>
                <ListItemText primary={suggestion} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Paper>
    </Popper>
  );
};

export default TaskAssistantSuggester;